import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  Card,
  CardHeader,
  CardBody,
  Divider,
  useToast,
  useMediaQuery,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  SimpleGrid,
  Image,
  Icon,
  useColorModeValue,
  Spinner,
  Badge
} from "@chakra-ui/react";
import { FaCheck, FaInfoCircle, FaDollarSign, FaMapMarkerAlt, FaCamera, FaVideo, FaTrash } from "react-icons/fa";
import { API_BASE_URL } from "./config/api_keys";
import SelectLocation from "./SellFormComponents/SelectLocation";

export default function EditAnimal() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [isMobile] = useMediaQuery("(max-width: 768px)");
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [animal, setAnimal] = useState(null);
  
  // Form states
  const [animalType, setAnimalType] = useState('');
  const [lactationCycle, setLactationCycle] = useState('');
  const [currentMilkPerDay, setCurrentMilkPerDay] = useState('');
  const [price, setPrice] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  
  // Media states
  const [videoUrl, setVideoUrl] = useState('');
  const [udderPhotoUrl, setUdderPhotoUrl] = useState('');
  const [milkingVideoUrl, setMilkingVideoUrl] = useState('');
  const [animalImages, setAnimalImages] = useState([]); // URLs from server
  const [previewImages, setPreviewImages] = useState([]); // Local previews
  const [existingImages, setExistingImages] = useState([]); // Existing images from server
  
  // Location states
  const [location, setLocation] = useState(null);
  const [address, setAddress] = useState(null);
  
  // Refs for file inputs
  const videoInputRef = useRef(null);
  const udderPhotoInputRef = useRef(null);
  const milkingVideoInputRef = useRef(null);
  const animalImagesInputRef = useRef(null);

  // Fetch animal data
  useEffect(() => {
    const fetchAnimal = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const response = await axios.get(`${API_BASE_URL}/api/animals/sell/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        
        const animalData = response.data;
        setAnimal(animalData);
        
        // Set form values
        setAnimalType(animalData.animalType || '');
        setLactationCycle(animalData.lactationCycle || '');
        setCurrentMilkPerDay(animalData.currentMilkPerDay || '');
        setPrice(animalData.price || '');
        setAdditionalInfo(animalData.additionalInfo || '');
        setTitle(animalData.title || '');
        setDescription(animalData.description || '');
        
        // Set media
        setVideoUrl(animalData.videoUrl || '');
        setUdderPhotoUrl(animalData.udderPhotoUrl || '');
        setMilkingVideoUrl(animalData.milkingVideoUrl || '');
        
        // Set images
        const allImages = [];
        if (animalData.images && animalData.images.length > 0) {
          allImages.push(...animalData.images);
        }
        if (animalData.udderPhotoUrl && !allImages.includes(animalData.udderPhotoUrl)) {
          allImages.push(animalData.udderPhotoUrl);
        }
        setExistingImages(allImages);
        
        // Set location
        if (animalData.location) {
          setLocation(animalData.location);
        }
        if (animalData.address) {
          setAddress(animalData.address);
        }
        
        setLoading(false);
      } catch (error) {
        console.error("Error fetching animal:", error);
        toast({
          title: "Error",
          description: "Failed to load animal data.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        navigate("/profile");
      }
    };
    
    fetchAnimal();
  }, [id, navigate, toast]);

  // Handle location change
  const handleLocationChange = (data) => {
    console.log('Location data received:', data);
    setLocation(data.location);
    setAddress(data.address);
  };

  // Handle multiple image upload
  const handleMultipleImageUpload = async (files) => {
    try {
      // Create preview images immediately
      const previewUrls = [];
      Array.from(files).forEach(file => {
        previewUrls.push(URL.createObjectURL(file));
      });
      setPreviewImages(prev => [...prev, ...previewUrls]);
      
      // Create FormData with all files
      const formData = new FormData();
      Array.from(files).forEach((file, index) => {
        formData.append('images', file);
      });
      
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        toast({
          title: 'Error',
          description: 'Please login to upload files',
          status: 'error',
          duration: 3000,
          isClosable: true
        });
        return;
      }
      
      const response = await axios.post(`${API_BASE_URL}/api/animals/upload-images`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Replace preview images with actual URLs from server
      setAnimalImages(prev => [...prev, ...response.data.imageUrls]);
      
      // Remove the preview images since we now have the real URLs
      setPreviewImages(prev => prev.filter(url => !previewUrls.includes(url)));
      
      toast({
        title: 'Success',
        description: `${files.length} image(s) uploaded successfully`,
        status: 'success',
        duration: 2000,
        isClosable: true
      });
    } catch (error) {
      // Remove preview images on error
      setPreviewImages(prev => prev.slice(0, prev.length - files.length));
      
      console.error('Error uploading images:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.error || error.response?.data?.details || 'Failed to upload images',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  };

  // Handle video upload
  const handleVideoUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append('video', file);
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        toast({
          title: 'Error',
          description: 'Please login to upload files',
          status: 'error',
          duration: 3000,
          isClosable: true
        });
        return;
      }
      
      const response = await axios.post(`${API_BASE_URL}/api/animals/upload-video`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setVideoUrl(response.data.videoUrl);
      toast({
        title: 'Success',
        description: 'Video uploaded successfully',
        status: 'success',
        duration: 2000,
        isClosable: true
      });
    } catch (error) {
      console.error('Error uploading video:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.error || error.response?.data?.details || 'Failed to upload video',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  };

  // Handle photo upload
  const handlePhotoUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append('photo', file);
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        toast({
          title: 'Error',
          description: 'Please login to upload files',
          status: 'error',
          duration: 3000,
          isClosable: true
        });
        return;
      }
      
      const response = await axios.post(`${API_BASE_URL}/api/animals/upload-photo`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setUdderPhotoUrl(response.data.photoUrl);
      toast({
        title: 'Success',
        description: 'Photo uploaded successfully',
        status: 'success',
        duration: 2000,
        isClosable: true
      });
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.error || error.response?.data?.details || 'Failed to upload photo',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  };

  // Handle milking video upload
  const handleMilkingVideoUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append('video', file);
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        toast({
          title: 'Error',
          description: 'Please login to upload files',
          status: 'error',
          duration: 3000,
          isClosable: true
        });
        return;
      }
      
      const response = await axios.post(`${API_BASE_URL}/api/animals/upload-video`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setMilkingVideoUrl(response.data.videoUrl);
      toast({
        title: 'Success',
        description: 'Milking video uploaded successfully',
        status: 'success',
        duration: 2000,
        isClosable: true
      });
    } catch (error) {
      console.error('Error uploading milking video:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.error || error.response?.data?.details || 'Failed to upload milking video',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  };

  // Remove image
  const removeImage = (index) => {
    const allImages = [...previewImages, ...animalImages, ...existingImages];
    const imageToRemove = allImages[index];
    
    // If it's a preview image (blob URL), revoke it
    if (imageToRemove.startsWith('blob:')) {
      URL.revokeObjectURL(imageToRemove);
      setPreviewImages(prev => prev.filter((_, i) => i !== (index - (existingImages.length + animalImages.length))));
    } else if (index >= existingImages.length + animalImages.length) {
      // It's an existing image
      setExistingImages(prev => prev.filter((_, i) => i !== (index - animalImages.length - previewImages.length)));
    } else if (index >= existingImages.length) {
      // It's a newly uploaded image
      setAnimalImages(prev => prev.filter((_, i) => i !== (index - existingImages.length)));
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // Combine all images
      const allImages = [...existingImages, ...animalImages];
      
      // Prepare data for update
      const updateData = {
        animalType,
        title,
        description,
        price: Number(price),
        lactationCycle,
        currentMilkPerDay: currentMilkPerDay ? Number(currentMilkPerDay) : undefined,
        videoUrl,
        udderPhotoUrl,
        milkingVideoUrl,
        images: allImages,
        additionalInfo,
        ...(location && { location }),
        ...(address && { address })
      };
      
      const token = localStorage.getItem('authToken');
      
      // Update the animal
      await axios.put(`${API_BASE_URL}/api/animals/sell/${id}`, updateData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      toast({
        title: "Animal Updated",
        description: "Your animal listing has been successfully updated.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      
      // Navigate back to profile animals tab
      navigate("/profile?tab=animals");
    } catch (error) {
      console.error("Error updating animal:", error);
      toast({
        title: "Error",
        description: error.response?.data?.error || "Failed to update animal.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
    
    setSubmitting(false);
  };

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="60vh"
      >
        <Spinner size="xl" color="green.500" />
      </Box>
    );
  }

  return (
    <Container maxW={isMobile ? "container.sm" : "container.md"} py={6}>
      <Card w="100%" overflow="hidden" boxShadow="md" borderRadius="lg">
        <CardHeader bg="green.500" color="white" py={4}>
          <Heading size="md" textAlign="center">
            Edit Animal Listing
          </Heading>
        </CardHeader>
        
        <CardBody p={6}>
          <form onSubmit={handleSubmit}>
            <VStack spacing={6} align="stretch">
              {/* Animal Type */}
              <Box>
                <HStack mb={2}>
                  <FaInfoCircle color="green" />
                  <Heading size="sm">Animal Type</Heading>
                </HStack>
                <HStack spacing={3}>
                  {['Cow', 'Buffalo', 'Other'].map((type) => (
                    <Button
                      key={type}
                      size="md"
                      borderRadius="full"
                      colorScheme={animalType === type ? 'green' : 'gray'}
                      variant={animalType === type ? 'solid' : 'outline'}
                      onClick={() => setAnimalType(type)}
                    >
                      {type}
                    </Button>
                  ))}
                </HStack>
              </Box>
              
              <Divider />
              
              {/* Lactation Cycle */}
              <Box>
                <HStack mb={2}>
                  <FaInfoCircle color="green" />
                  <Heading size="sm">Lactation Cycle</Heading>
                </HStack>
                <HStack spacing={3} flexWrap="wrap">
                  {[
                    { value: 'not_calved', label: 'Not Calved' },
                    { value: 'first', label: 'First Lactation' },
                    { value: 'second', label: 'Second Lactation' },
                    { value: 'other', label: 'Other' }
                  ].map((cycle) => (
                    <Button
                      key={cycle.value}
                      size="md"
                      borderRadius="full"
                      colorScheme={lactationCycle === cycle.value ? 'green' : 'gray'}
                      variant={lactationCycle === cycle.value ? 'solid' : 'outline'}
                      onClick={() => setLactationCycle(cycle.value)}
                    >
                      {cycle.label}
                    </Button>
                  ))}
                </HStack>
              </Box>
              
              <Divider />
              
              {/* Milk Production */}
              <Box>
                <HStack mb={2}>
                  <Icon as={FaInfoCircle} color="green" />
                  <Heading size="sm">Milk Production</Heading>
                </HStack>
                <FormControl>
                  <FormLabel>Current Milk Per Day (Liters)</FormLabel>
                  <NumberInput 
                    min={0} 
                    value={currentMilkPerDay || ""}
                    onChange={(value) => setCurrentMilkPerDay(value)}
                    placeholder="Enter milk production"
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
              </Box>
              
              <Divider />
              
              {/* Price */}
              <Box>
                <HStack mb={2}>
                  <FaDollarSign color="green" />
                  <Heading size="sm">Price</Heading>
                </HStack>
                <FormControl>
                  <FormLabel>Price (₹)</FormLabel>
                  <NumberInput 
                    min={0} 
                    value={price || ""}
                    onChange={(value) => setPrice(value)}
                    placeholder="Enter price"
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
              </Box>
              
              <Divider />
              
              {/* Title and Description */}
              <Box>
                <HStack mb={2}>
                  <FaInfoCircle color="green" />
                  <Heading size="sm">Listing Details</Heading>
                </HStack>
                <FormControl mb={4}>
                  <FormLabel>Title</FormLabel>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Enter title"
                  />
                </FormControl>
                
                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Enter description"
                    rows={3}
                  />
                </FormControl>
              </Box>
              
              <Divider />
              
              {/* Location */}
              <Box>
                <HStack mb={2}>
                  <FaMapMarkerAlt color="green" />
                  <Heading size="sm">Location</Heading>
                </HStack>
                <SelectLocation 
                  onAddressSelect={handleLocationChange} 
                  initialLocation={location}
                  initialAddress={address}
                />
              </Box>
              
              <Divider />
              
              {/* Media Upload */}
              <Box>
                <HStack mb={2}>
                  <FaCamera color="green" />
                  <Heading size="sm">Media</Heading>
                </HStack>
                
                <VStack spacing={4} align="stretch">
                  {/* Multiple Images Upload */}
                  <Box>
                    <Text mb={2} fontSize="sm" fontWeight="medium">Animal Images</Text>
                    <Box
                      borderWidth="2px"
                      borderStyle="dashed"
                      borderColor={borderColor}
                      borderRadius="lg"
                      p={4}
                      textAlign="center"
                    >
                      <input
                        ref={animalImagesInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const files = e.target.files;
                          if (files && files.length > 0) handleMultipleImageUpload(files);
                        }}
                      />
                      <Button
                        colorScheme="blue"
                        size="sm"
                        onClick={() => animalImagesInputRef.current?.click()}
                        leftIcon={<Icon as={FaCamera} />}
                      >
                        Choose Images
                      </Button>
                      <Text fontSize="xs" color="gray.500" mt={2}>
                        You can select multiple images at once
                      </Text>
                    </Box>
                    
                    {/* Preview of uploaded images */}
                    {(previewImages.length > 0 || animalImages.length > 0 || existingImages.length > 0) && (
                      <SimpleGrid columns={3} spacing={2} mt={3}>
                        {[...existingImages, ...animalImages, ...previewImages].map((img, index) => (
                          <Box key={index} position="relative">
                            <Image 
                              src={img} 
                              alt={`Animal ${index + 1}`} 
                              borderRadius="md" 
                              objectFit="cover"
                              h="100px"
                              w="100%"
                              border={img.startsWith('blob:') ? "2px dashed #3182ce" : "none"}
                            />
                            <Button
                              size="xs"
                              colorScheme="red"
                              position="absolute"
                              top={1}
                              right={1}
                              onClick={() => removeImage(index)}
                            >
                              <Icon as={FaTrash} />
                            </Button>
                          </Box>
                        ))}
                      </SimpleGrid>
                    )}
                  </Box>
                  
                  {/* Video and Photo in two columns */}
                  <HStack spacing={4} align="stretch">
                    {/* Video Upload */}
                    <Box
                      flex={1}
                      borderWidth="2px"
                      borderStyle="dashed"
                      borderColor={borderColor}
                      borderRadius="lg"
                      p={4}
                      textAlign="center"
                    >
                      <Text mb={2} fontSize="sm" fontWeight="medium">Animal Video</Text>
                      {videoUrl ? (
                        <VStack>
                          <Badge colorScheme="green">Uploaded</Badge>
                          <Button size="xs" onClick={() => videoInputRef.current?.click()}>
                            Change
                          </Button>
                        </VStack>
                      ) : (
                        <>
                          <Icon as={FaVideo} boxSize={6} color="gray.400" mb={2} />
                          <Button
                            colorScheme="blue"
                            size="sm"
                            onClick={() => videoInputRef.current?.click()}
                          >
                            Choose Video
                          </Button>
                        </>
                      )}
                      <input
                        ref={videoInputRef}
                        type="file"
                        accept="video/*"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) handleVideoUpload(file);
                        }}
                      />
                    </Box>
                    
                    {/* Udder Photo Upload */}
                    <Box
                      flex={1}
                      borderWidth="2px"
                      borderStyle="dashed"
                      borderColor={borderColor}
                      borderRadius="lg"
                      p={4}
                      textAlign="center"
                    >
                      <Text mb={2} fontSize="sm" fontWeight="medium">Udder Photo</Text>
                      {udderPhotoUrl ? (
                        <VStack>
                          <Badge colorScheme="green">Uploaded</Badge>
                          <Button size="xs" onClick={() => udderPhotoInputRef.current?.click()}>
                            Change
                          </Button>
                        </VStack>
                      ) : (
                        <>
                          <Icon as={FaCamera} boxSize={6} color="gray.400" mb={2} />
                          <Button
                            colorScheme="blue"
                            size="sm"
                            onClick={() => udderPhotoInputRef.current?.click()}
                          >
                            Choose Photo
                          </Button>
                        </>
                      )}
                      <input
                        ref={udderPhotoInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) handlePhotoUpload(file);
                        }}
                      />
                    </Box>
                  </HStack>
                  
                  {/* Milking Video Upload */}
                  <Box>
                    <Text mb={2} fontSize="sm" fontWeight="medium">Milking Video</Text>
                    {milkingVideoUrl ? (
                      <HStack>
                        <Badge colorScheme="green">Video Uploaded</Badge>
                        <Button size="sm" onClick={() => milkingVideoInputRef.current?.click()}>
                          Change
                        </Button>
                      </HStack>
                    ) : (
                      <Box
                        borderWidth="2px"
                        borderStyle="dashed"
                        borderColor={borderColor}
                        borderRadius="lg"
                        p={4}
                        textAlign="center"
                      >
                        <Icon as={FaVideo} boxSize={6} color="gray.400" mb={2} />
                        <Button
                          colorScheme="blue"
                          size="sm"
                          onClick={() => milkingVideoInputRef.current?.click()}
                        >
                          Choose Milking Video
                        </Button>
                      </Box>
                    )}
                    <input
                      ref={milkingVideoInputRef}
                      type="file"
                      accept="video/*"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) handleMilkingVideoUpload(file);
                      }}
                    />
                  </Box>
                </VStack>
              </Box>
              
              <Divider />
              
              {/* Additional Information */}
              <Box>
                <HStack mb={2}>
                  <FaInfoCircle color="green" />
                  <Heading size="sm">Additional Information</Heading>
                </HStack>
                <FormControl>
                  <Textarea
                    value={additionalInfo}
                    onChange={(e) => setAdditionalInfo(e.target.value)}
                    placeholder="Add any additional information about the animal"
                    rows={4}
                  />
                </FormControl>
              </Box>
              
              <Divider />
              
              {/* Submit Button */}
              <HStack justifyContent="space-between">
                <Button
                  variant="outline"
                  colorScheme="gray"
                  onClick={() => navigate("/profile?tab=animals")}
                >
                  Cancel
                </Button>
                
                <Button
                  colorScheme="green"
                  type="submit"
                  isLoading={submitting}
                  leftIcon={<FaCheck />}
                >
                  Update Animal
                </Button>
              </HStack>
            </VStack>
          </form>
        </CardBody>
      </Card>
    </Container>
  );
}
